import I18n from 'I18n';
import { useQuery } from 'data-fetching-client';
import FormattedPercentage from 'I18n/components/FormattedPercentage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import SkeletonText from 'automation-ui-core/components/SkeletonText';
import PropertyName from 'automation-ui-references/object-type-properties/PropertyName';
import { GET_OBJECT_TYPE_BY_ID } from 'automation-ui-references/object-type-properties/ObjectTypesQueries';
import { INCREASE } from 'automation-ui-action-features/actions/set-property/SetPropertyActionOperations';
import { useTargetProperty } from 'automation-ui-action-features/actions/set-property/wireTargetProperty';
import * as PropertyValueTypes from 'automation-ui-flow-core/fields/PropertyValueTypes';
import * as SetPropertyActionOperations from 'automation-ui-action-features/actions/set-property/SetPropertyActionOperations';
import associationSupportsCustomLabels from 'automation-ui-associations/utils/associationSupportsCustomLabels';
import CRMPropertyFieldTypes from 'automation-ui-flow-core/fields/CRMPropertyFieldTypes';
import getAssociationLabel from 'automation-ui-associations/utils/getAssociationLabel';
import findAssociationDefinitionFromSpec from 'automation-ui-associations/utils/findAssociationDefinitionFromSpec';
import inferPropertyActionOperationType from 'automation-ui-action-features/actions/set-property/utils/inferPropertyActionOperationType';
import { getObjectTypeNameMessageOptions } from 'automation-ui-action-features/actions/set-property/utils/getObjectTypeNameMessageOptions';
import { useExternalOptions } from 'automation-ui-action-features/external-options/externalOptionsDecorator';
import PersonalizedTextView from 'automation-ui-action-features/personalized-text/PersonalizedTextView';
import DataSourcePropertyName from 'automation-ui-formula-editor/data-sources/DataSourcePropertyName';
import DataSourceName from 'automation-ui-formula-editor/data-sources/DataSourceName';
import { useAssociationDefinitions } from 'automation-ui-associations/hooks/associationDefinitionsHooks';
import getInputTypeDefinition from 'automation-ui-action-features/action-outputs/utils/getInputTypeDefinition';
import { getIsAssociation } from 'automation-ui-action-features/actions/set-property/utils/associationUtils';
import { jsx as _jsx } from "react/jsx-runtime";
export default function SetPropertyActionExplainer({
  action,
  flowObjectTypeId,
  getActionLabelById,
  getActionOutputsForTargetActionById
}) {
  const actionId = action.getActionId();
  const actionMetadata = action.metadata;
  const associationDefinitionsQuery = useAssociationDefinitions([flowObjectTypeId]);
  const {
    // targetObjectTypeId will be undefined until association definitions are loaded
    targetObjectTypeId,
    targetProperty,
    targetPropertyValueLabel
  } = useTargetProperty({
    actionMetadata,
    associationDefinitionsQuery,
    flowObjectTypeId
  });
  const {
    data: {
      objectTypeById: targetObjectTypeDefinition = null
    } = {}
  } = useQuery(GET_OBJECT_TYPE_BY_ID, {
    skip: !targetObjectTypeId,
    variables: {
      objectTypeId: targetObjectTypeId
    }
  });
  const operation = inferPropertyActionOperationType(actionMetadata);
  const valueType = actionMetadata.getValueType();
  const targetPropertyName = actionMetadata.getTargetPropertyName();
  const sourcePropertyName = actionMetadata.getValuePropertyName();
  const isNotFieldData = valueType !== PropertyValueTypes.FIELD_DATA;
  const isPercentageValue = !!targetProperty && targetProperty.fieldType === CRMPropertyFieldTypes.NUMBER && targetProperty.numberDisplayHint === 'percentage';

  // External Options value takes precedence.
  const {
    references: externalOptionReferences
  } = useExternalOptions({
    property: targetProperty,
    propertyTypeId: targetObjectTypeId,
    externalOptionValue: actionMetadata.getValueValue()
  });
  const valueOverride = externalOptionReferences ? I18n.formatList(externalOptionReferences.map(reference => reference.label)) : targetPropertyValueLabel || '';
  const isAssociation = getIsAssociation({
    metadata: actionMetadata
  });
  const isIncreaseAction = operation === INCREASE;
  const showAssociationLabel = targetObjectTypeId && isAssociation && associationSupportsCustomLabels(flowObjectTypeId, targetObjectTypeId) && !isIncreaseAction;
  const sourceActionId = actionMetadata.getValueIdentifierActionId();
  const outputDefinition = getInputTypeDefinition({
    sourceActionId: sourceActionId,
    sourceOutputName: actionMetadata.getValueIdentifierDataKey(),
    actionOutputs: getActionOutputsForTargetActionById(actionId, false)
  });
  const messageOptions = Object.assign({
    inputActionLabel: /*#__PURE__*/_jsx("strong", {
      children: getActionLabelById({
        actionId: sourceActionId
      })
    }),
    inputValueName: outputDefinition ? /*#__PURE__*/_jsx("strong", {
      children: outputDefinition.label
    }) : /*#__PURE__*/_jsx(SkeletonText, {}),
    inputDataPropertyName: /*#__PURE__*/_jsx("strong", {
      children: /*#__PURE__*/_jsx(DataSourcePropertyName, {
        token: actionMetadata.getValuePropertyToken()
      })
    }),
    inputDataSourceLabel: /*#__PURE__*/_jsx("strong", {
      children: /*#__PURE__*/_jsx(DataSourceName, {
        token: actionMetadata.getValuePropertyToken()
      })
    }),
    associatedLabel: showAssociationLabel && /*#__PURE__*/_jsx("strong", {
      children: targetObjectTypeDefinition ? getAssociationLabel({
        associationDefinition: findAssociationDefinitionFromSpec(actionMetadata.getTargetAssociation(), associationDefinitionsQuery),
        toObjectTypeDefinition: targetObjectTypeDefinition
      }) : /*#__PURE__*/_jsx(SkeletonText, {})
    }),
    amount: /*#__PURE__*/_jsx("strong", {
      children: Math.abs(actionMetadata.getValueAmount())
    })
  }, getObjectTypeNameMessageOptions({
    flowObjectTypeId,
    targetObjectTypeId
  }), {
    sourcePropertyName: sourcePropertyName && isNotFieldData ? /*#__PURE__*/_jsx(PropertyName, {
      propertyName: sourcePropertyName,
      objectTypeId: flowObjectTypeId
    }) : null,
    targetPropertyName: targetObjectTypeId && targetPropertyName ? /*#__PURE__*/_jsx(PropertyName, {
      propertyName: targetPropertyName,
      objectTypeId: targetObjectTypeId
    }) : /*#__PURE__*/_jsx(SkeletonText, {}),
    value: /*#__PURE__*/_jsx("strong", {
      children: isPercentageValue ? /*#__PURE__*/_jsx(FormattedPercentage, {
        precision: 2,
        value: Number(valueOverride) * 100
      }) : /*#__PURE__*/_jsx(PersonalizedTextView, {
        propertyTypeId: flowObjectTypeId,
        text: valueOverride
      })
    })
  });
  let message = '';
  if (operation === SetPropertyActionOperations.INCREASE) {
    const isIncreasing = actionMetadata.getValueAmount() >= 0;
    if (valueType === PropertyValueTypes.INCREMENT) {
      if (isIncreasing) {
        message = isAssociation ? 'automation-ui-action-features.action-features.set-property.INCREASE.explainer.associated.increase' : 'automation-ui-action-features.action-features.set-property.INCREASE.explainer.enrolled.increase';
      } else {
        message = isAssociation ? 'automation-ui-action-features.action-features.set-property.INCREASE.explainer.associated.decrease' : 'automation-ui-action-features.action-features.set-property.INCREASE.explainer.enrolled.decrease';
      }
    } else {
      message = isAssociation ? 'automation-ui-action-features.action-features.set-property.INCREASE.explainer.associated' : 'automation-ui-action-features.action-features.set-property.INCREASE.explainer.enrolled';
    }
  } else if (actionMetadata.getIsValueEmpty()) {
    message = isAssociation ? 'automation-ui-action-features.action-features.set-property.SET.explainer.clear-property.associated' : 'automation-ui-action-features.action-features.set-property.SET.explainer.clear-property.enrolled';
  } else {
    // operation === SetPropertyActionOperations.SET
    if (valueType === PropertyValueTypes.STATIC_APPEND_VALUE) {
      if (isAssociation) {
        message = showAssociationLabel ? 'automation-ui-action-features.action-features.set-property.SET.explainer.static-append-value.associated-label' : 'automation-ui-action-features.action-features.set-property.SET.explainer.static-append-value.associated';
      } else {
        message = 'automation-ui-action-features.action-features.set-property.SET.explainer.static-append-value.enrolled';
      }
    } else if (valueType === PropertyValueTypes.TIMESTAMP) {
      if (isAssociation) {
        message = showAssociationLabel ? 'automation-ui-action-features.action-features.set-property.SET.explainer.date-of-step.associated-label' : 'automation-ui-action-features.action-features.set-property.SET.explainer.date-of-step.associated';
      } else {
        message = 'automation-ui-action-features.action-features.set-property.SET.explainer.date-of-step.enrolled';
      }
    } else if (valueType === PropertyValueTypes.OBJECT_PROPERTY) {
      if (isAssociation) {
        message = showAssociationLabel ? 'automation-ui-action-features.action-features.set-property.SET.explainer.object-property-value-associated-label' : 'automation-ui-action-features.action-features.set-property.SET.explainer.object-property-value-associated';
      } else {
        message = 'automation-ui-action-features.action-features.set-property.SET.explainer.object-property-value';
      }
    } else if (valueType === PropertyValueTypes.FETCHED_OBJECT_PROPERTY || valueType === PropertyValueTypes.ENROLLMENT_EVENT_PROPERTY) {
      if (isAssociation) {
        message = showAssociationLabel ? 'automation-ui-action-features.action-features.set-property.SET.explainer.data-token.associated-label' : 'automation-ui-action-features.action-features.set-property.SET.explainer.data-token.associated';
      } else {
        message = 'automation-ui-action-features.action-features.set-property.SET.explainer.data-token.enrolled';
      }
    } else {
      if (isAssociation) {
        message = showAssociationLabel ? 'automation-ui-action-features.action-features.set-property.SET.explainer.associated-label' : 'automation-ui-action-features.action-features.set-property.SET.explainer.associated';
      } else {
        message = 'automation-ui-action-features.action-features.set-property.SET.explainer.enrolled';
      }
    }
  }
  return /*#__PURE__*/_jsx(FormattedReactMessage, {
    message: message,
    options: messageOptions
  });
}